import type { GetTrialsPatientsResponseDtoType } from '@tempus/trials-types';
import type { AxiosRequestConfig } from 'axios';

import axios from '../axios';
import { TRIALS_PATIENTS } from '../constants';
import type {
  SiteTrialsPatientReasonDetail,
  SiteTrialsPatientsListRequest,
  SiteTrialsPatientsTabCountsRequest,
  SiteTrialsPatientsTabCountsResponse,
  SiteTrialsPatientTableCohort,
  SiteTrialsPatientTableRow,
  SiteTrialsPatientUpcomingVisit,
} from '../types/siteTrialsPatients';

type SiteTrialsPatientsApiRow = Omit<SiteTrialsPatientTableRow, 'patientLinkageId' | 'upcomingVisit'> & {
  patientLinkageId?: string | null;
  upcomingVisit?: SiteTrialsPatientUpcomingVisit | null;
};

type SiteTrialsPatientsListResponse = Omit<GetTrialsPatientsResponseDtoType, 'patients'> & {
  patients: SiteTrialsPatientsApiRow[];
};

type SiteTrialsPatientsResponse = Omit<GetTrialsPatientsResponseDtoType, 'patients'> & {
  patients: SiteTrialsPatientTableRow[];
};

const normalizePatientLinkageId = (patientLinkageId: string | null | undefined): string =>
  patientLinkageId?.trim() ?? '';

const normalizeUpcomingVisit = (
  visit: SiteTrialsPatientUpcomingVisit | null | undefined,
): SiteTrialsPatientUpcomingVisit | null => {
  if (!visit?.visitDate && !visit?.visitType) {
    return null;
  }

  return {
    visitDate: visit.visitDate ?? null,
    visitType: visit.visitType ?? null,
  };
};

const trimOptionalString = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  if (!trimmed || trimmed === '&nbsp;' || trimmed === '\u00a0') {
    return null;
  }
  return trimmed;
};

const normalizeNotACandidateReason = (
  reason: SiteTrialsPatientReasonDetail | null | undefined,
): SiteTrialsPatientReasonDetail | null => {
  const label = trimOptionalString(reason?.label);
  const date = trimOptionalString(reason?.date);
  if (!label && !date) {
    return null;
  }

  return { label, date };
};

const normalizeSiteTrialsPatientCohort = (cohort: SiteTrialsPatientTableCohort): SiteTrialsPatientTableCohort => {
  const notACandidateReason = normalizeNotACandidateReason(cohort.notACandidateReason);
  const consentedDate = trimOptionalString(cohort.consentedDate);
  const enrolledDate = trimOptionalString(cohort.enrolledDate);
  const reviewAgainDate = trimOptionalString(cohort.reviewAgainDate);
  const watchlistNote = trimOptionalString(cohort.watchlistNote);

  return {
    armName: cohort.armName,
    status: cohort.status,
    ...(notACandidateReason ? { notACandidateReason } : {}),
    ...(consentedDate ? { consentedDate } : {}),
    ...(enrolledDate ? { enrolledDate } : {}),
    ...(reviewAgainDate ? { reviewAgainDate } : {}),
    ...(watchlistNote ? { watchlistNote } : {}),
  };
};

const uncachedPost = { cache: false } as AxiosRequestConfig;

export const siteTrialsPatientsApi = {
  getPatients: async (body: SiteTrialsPatientsListRequest): Promise<SiteTrialsPatientsResponse> => {
    const { data } = await axios.post<SiteTrialsPatientsListResponse>(TRIALS_PATIENTS, body, uncachedPost);
    return {
      ...data,
      patients: data.patients.map((patient) => ({
        ...patient,
        patientLinkageId: normalizePatientLinkageId(patient.patientLinkageId),
        upcomingVisit: normalizeUpcomingVisit(patient.upcomingVisit),
        cohorts: patient.cohorts.map(normalizeSiteTrialsPatientCohort),
      })),
    };
  },
  getTabCounts: async (body: SiteTrialsPatientsTabCountsRequest): Promise<SiteTrialsPatientsTabCountsResponse> => {
    const { data } = await axios.post<SiteTrialsPatientsTabCountsResponse>(
      `${TRIALS_PATIENTS}/tab-counts`,
      body,
      uncachedPost,
    );
    return data;
  },
};
